import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import { faCopyToOne } from '@epicuro-next/ataraxia/icons';
import { UTCDeliveryPeriod } from '@epicuro-next/ataraxia/models';
import {
  ConfigStripConfigurationKeys,
  ConfigurationsService,
  CONFIG_STRIP_FEATURE,
} from '@epicuro-next/platform/configuration';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { DeliveryWindowType } from 'graphql-api/gql-api-interfaces';
import { BehaviorSubject, Subject } from 'rxjs';
import Timeout = NodeJS.Timeout;

/**
 * Component responsible for displaying a delivery period: Date and quantity
 */
@Component({
  selector: 'epc-delivery-period',
  templateUrl: './delivery-period.component.html',
  styleUrls: ['./delivery-period.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DeliveryPeriodComponent implements OnChanges, OnDestroy {
  public readonly monthInterval = DeliveryWindowType.Month;
  public readonly weekInterval = DeliveryWindowType.Week;
  public readonly dayInterval = DeliveryWindowType.Day;
  public pasteTooltipMessage = $localize`:Config Strip|Paste delivery period tooltip message@@pasteDeliveryPeriodTooltipMessage:This will copy all quantities from the currently active delivery period`;
  public showAnimation$ = new BehaviorSubject(false);
  private animationTimeout?: Timeout;

  /** Information about the delivery period: dates */
  @Input() public deliveryData?: string | UTCDeliveryPeriod;

  @Input() public intervalType: DeliveryWindowType = this.dayInterval;

  /**
   * Display a custom label instead of the delivery month
   */
  @Input() public customLabel?: string;

  /*
   * Display a custom placeholder when quantity === 0
   * */
  @Input() public placeholderLabel: string;

  @Input()
  public differentLines = true;

  /** quantity to show */
  @Input() public quantity = 0;

  /** show/hide the paste icon */
  @Input() public showPaste = false;

  /** show/hide the paste icon */
  @Input() public showCopied = false;

  @Input() public showDelete = false;

  @Input() public disabled = false;

  /** paste icon type */
  @Input() public pasteIcon = this.configurationsService.getConfigValue({
    featureName: ConfigStripConfigurationKeys.SingleSizeCopyIcon,
    namespace: CONFIG_STRIP_FEATURE,
    defaultValue: faCopyToOne,
  });

  @Input() public trashIcon = faCircleXmark;

  /** copied icon type */
  @Input() public copiedIcon = faCircleCheck;

  /** it notifies the paste icon click */
  @Output() public pastedQuantities = new EventEmitter<void>();

  @Output()
  public deletePeriod = new EventEmitter<string | UTCDeliveryPeriod>();

  protected destroy$ = new Subject<void>();

  constructor(protected configurationsService: ConfigurationsService) {}

  public pasteQuantities(event: Event) {
    event.stopPropagation();
    this.pastedQuantities.emit();
  }

  public onDelete(event: Event) {
    event.stopImmediatePropagation();
    this.deletePeriod.emit(this.deliveryData);
  }

  public ngOnChanges() {
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
    this.showAnimation$.next(true);
    this.animationTimeout = setTimeout(() => {
      this.showAnimation$.next(false);
    }, 3000);
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
  }
}
